export type CanvasSize = { width: number; height: number };

const DEFAULT_MAX_SHORT_SIDE = 2160; // 2160p (4K)
const DEFAULT_MAX_LONG_SIDE = 4096; // allow DCI-ish widths while capping GPU load

// Standard tiers (short-side in pixels). Tier drives max export resolution.
export const CANVAS_TIER_PRESETS = [480, 720, 1080, 1440, 2160] as const;
export type CanvasTierPreset = (typeof CANVAS_TIER_PRESETS)[number];

export type CanvasAspectPreset = "horizontal" | "vertical" | "square";

export function inferCanvasAspectPreset(width: number, height: number): CanvasAspectPreset {
  const w = Number(width);
  const h = Number(height);
  if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) return "horizontal";
  const ratio = w / h;
  if (Math.abs(ratio - 1) < 0.06) return "square";
  return ratio < 1 ? "vertical" : "horizontal";
}

/**
 * Pick a standard tier from an observed short-side value.
 * Uses a "floor, but allow small round-up" heuristic to avoid odd near-misses
 * (e.g. 1072px should map to 1080 tier).
 */
export function pickCanvasTierFromShortSide(
  shortSide: number,
  opts?: { roundUpTolerance?: number }
): CanvasTierPreset {
  const s = Number(shortSide);
  const tol = opts?.roundUpTolerance ?? 0.02; // 2%
  if (!Number.isFinite(s) || s <= 0) return CANVAS_TIER_PRESETS[2]; // 1080 default

  // Floor to the highest tier <= s (or smallest tier if s is tiny).
  let tier: CanvasTierPreset = CANVAS_TIER_PRESETS[0];
  for (const t of CANVAS_TIER_PRESETS) {
    if (s >= t) tier = t;
    else break;
  }

  // If we're just below the next tier (within tolerance), round up.
  const idx = CANVAS_TIER_PRESETS.indexOf(tier);
  const next = idx >= 0 ? CANVAS_TIER_PRESETS[idx + 1] : undefined;
  if (next && s >= next * (1 - tol)) {
    return next;
  }
  return tier;
}

export function getCanvasTierFromCanvasSize(width: number, height: number): CanvasTierPreset {
  const shortSide = Math.min(Number(width), Number(height));
  return pickCanvasTierFromShortSide(shortSide);
}

export function canvasSizeForPresetAndTier(preset: CanvasAspectPreset, tier: number): CanvasSize {
  const t = Number(tier);
  const safeTier = Number.isFinite(t) && t > 0 ? t : 1080;
  const long = toEvenUp((safeTier * 16) / 9);

  if (preset === "square") {
    return { width: safeTier, height: safeTier };
  }
  if (preset === "vertical") {
    return { width: safeTier, height: long };
  }
  return { width: long, height: safeTier };
}

export function snapCanvasSizeToPreset(width: number, height: number): CanvasSize {
  const preset = inferCanvasAspectPreset(width, height);
  const tier = pickCanvasTierFromShortSide(Math.min(Number(width), Number(height)));
  return canvasSizeForPresetAndTier(preset, tier);
}

export function toEvenUp(value: number): number {
  const rounded = Math.round(value);
  return rounded % 2 === 0 ? rounded : rounded + 1;
}

export function normalizeCanvasSizeFromFirstVideo(
  sourceWidth: number,
  sourceHeight: number,
  opts?: { maxShortSide?: number; maxLongSide?: number }
): CanvasSize {
  const maxShortSide = opts?.maxShortSide ?? DEFAULT_MAX_SHORT_SIDE;
  const maxLongSide = opts?.maxLongSide ?? DEFAULT_MAX_LONG_SIDE;

  let w = Number(sourceWidth);
  let h = Number(sourceHeight);
  if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) {
    return { width: 0, height: 0 };
  }

  const longSide = Math.max(w, h);
  const shortSide = Math.min(w, h);

  const scale = Math.min(
    1,
    maxLongSide > 0 ? maxLongSide / longSide : 1,
    maxShortSide > 0 ? maxShortSide / shortSide : 1
  );

  w = w * scale;
  h = h * scale;

  // Ensure AVC/H.264-friendly sizes and avoid shrinking the source by rounding down.
  const evenW = Math.max(2, toEvenUp(w));
  const evenH = Math.max(2, toEvenUp(h));
  return { width: evenW, height: evenH };
}

export function compositionHasAnyVideo(composition: any): boolean {
  try {
    const tracks = composition?.tracks ?? [];
    for (const tr of tracks) {
      const clips = tr?.clips ?? [];
      for (const clip of clips) {
        const type = String(clip?.type ?? "").toLowerCase();
        if (type === "video") return true;
        if (type === "script") {
          try {
            const sources = clip?.getVideoSources?.();
            if (Array.isArray(sources) && sources.length > 0) return true;
          } catch {}
        }
      }
    }
  } catch {}
  return false;
}

export function compositionHasAnyVisual(composition: any): boolean {
  try {
    const tracks = composition?.tracks ?? [];
    for (const tr of tracks) {
      const clips = tr?.clips ?? [];
      for (const clip of clips) {
        const type = String(clip?.type ?? "").toLowerCase();
        if (type === "video" || type === "image") return true;
        if (type === "script") {
          try {
            const sources = clip?.getVideoSources?.();
            if (Array.isArray(sources) && sources.length > 0) return true;
          } catch {}
        }
      }
    }
  } catch {}
  return false;
}

export function maybeLockCanvasToFirstVideo(
  composition: any,
  sourceWidth: number,
  sourceHeight: number,
  opts?: { maxShortSide?: number; maxLongSide?: number }
): boolean {
  if (!composition || typeof composition.resize !== "function") return false;
  if (!Number.isFinite(sourceWidth) || !Number.isFinite(sourceHeight)) return false;
  if (!(sourceWidth > 0 && sourceHeight > 0)) return false;
  // Only allow first-video locking when there is no existing visual content at all.
  // If an image (or any other visual) was added first, keep the existing canvas resolution
  // and fit subsequent videos into it to avoid unexpected reframing.
  if (compositionHasAnyVisual(composition)) return false;

  const maxShortSide = opts?.maxShortSide ?? DEFAULT_MAX_SHORT_SIDE;
  const maxLongSide = opts?.maxLongSide ?? DEFAULT_MAX_LONG_SIDE;

  // Derive a standard tier and snap the canvas to the 3 supported aspect presets.
  const preset = inferCanvasAspectPreset(sourceWidth, sourceHeight);
  const observedShortSide = Math.min(sourceWidth, sourceHeight);

  let tier = pickCanvasTierFromShortSide(observedShortSide);
  // Respect explicit caps (best-effort).
  while (tier > maxShortSide) {
    const idx = CANVAS_TIER_PRESETS.indexOf(tier);
    const next = idx > 0 ? CANVAS_TIER_PRESETS[idx - 1] : undefined;
    if (!next) break;
    tier = next;
  }

  let target = canvasSizeForPresetAndTier(preset, tier);
  while (Math.max(target.width, target.height) > maxLongSide) {
    const idx = CANVAS_TIER_PRESETS.indexOf(tier);
    const next = idx > 0 ? CANVAS_TIER_PRESETS[idx - 1] : undefined;
    if (!next) break;
    tier = next;
    target = canvasSizeForPresetAndTier(preset, tier);
  }

  if (!(target.width > 0 && target.height > 0)) return false;

  try {
    composition.resize(target.width, target.height);
    return true;
  } catch {
    return false;
  }
}

export function fitToCanvas(
  sourceWidth: number,
  sourceHeight: number,
  canvasWidth: number,
  canvasHeight: number
): CanvasSize {
  const sw = Number(sourceWidth);
  const sh = Number(sourceHeight);
  const cw = Number(canvasWidth);
  const ch = Number(canvasHeight);

  if (!(sw > 0 && sh > 0 && cw > 0 && ch > 0)) {
    return { width: cw, height: ch };
  }

  const srcAspect = sw / sh;
  const canvasAspect = cw / ch;

  if (srcAspect > canvasAspect) {
    // Fit by width
    return { width: cw, height: cw / srcAspect };
  }
  // Fit by height
  return { width: ch * srcAspect, height: ch };
}
